import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { buddiesEligibilityThreshold } from 'src/replicant/buddies/buddies.ruleset';

type BuddiesBakerScoreOpts = ViewOpts & {
  onTap?: () => void;
};

export class BuddiesBakerScore extends View {
  private base: ImageScaleView;
  private strawberry: ImageScaleView;
  private alertBadge: ImageScaleView;
  private score: LangBitmapFontTextView;
  private scoreAdded: LangBitmapFontTextView;
  private scoreValue = 0;
  private scoreAddedValue = 0;

  constructor(opts?: BuddiesBakerScoreOpts) {
    super({
      width: 133,
      height: 35,
      centerOnOrigin: true,
      canHandleEvents: true,
      ...opts,
    });

    if (opts?.onTap) {
      this.onInputStart = () => opts.onTap!();
    }

    this.base = new ImageScaleView({
      superview: this,
      image: 'assets/ui/buddies/common/label_base.png',
      width: this.style.width,
      height: this.style.height,
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height / 2,
    });

    this.strawberry = new ImageScaleView({
      superview: this,
      image: 'assets/ui/buddies/common/strawberry.png',
      width: 45,
      height: 51,
      x: 10,
      y: this.style.height / 2,
      centerOnOrigin: true,
    });

    this.alertBadge = new ImageScaleView({
      superview: this,
      image: 'assets/ui/shared/icons/alert_icon_red.png',
      scaleMethod: 'contain',
      width: 50,
      height: 50,
      x: this.style.width - 10,
      y: this.style.height / 2,
      centerOnOrigin: true,
      visible: false,
    });

    this.score = new LangBitmapFontTextView({
      superview: this,
      x: this.style.width / 2 + 5,
      y: this.style.height / 2 - 1,
      align: 'center',
      verticalAlign: 'center',
      anchorY: 0.5,
      size: 26,
      font: 'Title',
      color: '#6AF6FF',
      width: this.style.width,
      height: this.style.height,
      centerOnOrigin: true,
      localeText: () => '0000',
    });

    this.scoreAdded = new LangBitmapFontTextView({
      superview: this,
      x: this.style.width / 2 + 5,
      y: -20,
      align: 'center',
      verticalAlign: 'center',
      anchorY: 0.5,
      size: 32,
      font: 'Title',
      color: '#6AF6FF',
      width: this.style.width,
      height: this.style.height,
      centerOnOrigin: true,
      visible: false,
      localeText: () => '+10',
    });
  }

  public setScore(score: number, added: number) {
    this.show();
    const pastScore = Math.max(0, score - added);
    // Color reflects current eligibility (final score), even though we initially
    // display pastScore before animating to the final value
    const isIneligible = score < buddiesEligibilityThreshold;
    const color = isIneligible ? '#FF6666' : '#6AF6FF';
    this.score.color = color;
    this.alertBadge.updateOpts({ visible: isIneligible });
    this.score.localeText = () => `${pastScore}`;
    this.scoreAdded.localeText = () => `+${added}`;
    this.scoreAdded.updateOpts({ visible: false });
    this.scoreValue = score;
    this.scoreAddedValue = added;
  }

  public clearScore() {
    this.hide();
  }

  public async animateScore() {
    if (!this.scoreValue) return;
    if (!this.scoreAddedValue) return;
    this.scoreAdded.updateOpts({ scale: 0, y: -15, opacity: 1, visible: true });
    sounds.sfx.play('gachaOpenCapsule');
    await animate(this.scoreAdded)
      .now({ scale: 1 }, 300, animate.easeOutBack)
      .toPromise();
    await animate(this.scoreAdded).now({ scale: 1 }, 500).toPromise();
    this.score.localeText = () => `${this.scoreValue}`;
    sounds.sfx.play('progressAwarded');
    await animate(this.scoreAdded).now({ y: -60, opacity: 0 }, 500).toPromise();
    this.scoreAdded.updateOpts({ visible: false });
  }
}
